<template>
  <div v-if="visible" class="modal-overlay" @click.self="handleClose">
    <div class="credentials-modal">
      <div class="modal-header">
        <div class="warning-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#faad14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h2 class="modal-title">{{ $t("message.guest_credentials_title") }}</h2>
      </div>

      <div class="credentials-content">
        <div class="credential-item">
          <label class="credential-label">{{ $t("message.account") }}</label>
          <div class="credential-value">
            <span class="value-text">{{ displayUid() }}</span>
            <button class="copy-btn" @click="copyToClipboard(displayUid())">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="credential-item">
          <label class="credential-label">{{ $t("message.password") }}</label>
          <div class="credential-value">
            <span class="value-text">{{ displayPassword() }}</span>
            <button class="copy-btn" @click="copyToClipboard(displayPassword())">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div class="warning-message">
        <p>{{ $t("message.guest_credentials_warning") }}</p>
      </div>

      <button class="confirm-btn" @click="handleConfirm">
        {{ $t("message.i_have_saved") }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { toastSuccess } from '@/utils/common'
import emitter from '@/utils/bus'

const { t } = useI18n()
const $swal = inject('$swal')

const props = defineProps({
  uid: {
    type: String,
    default: ''
  },
  password: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['close', 'confirm'])

const visible = ref(false)
const localUid = ref('')
const localPassword = ref('')

const show = (data) => {
  if (data) {
    localUid.value = data.uid || ''
    localPassword.value = data.password || ''
  }
  visible.value = true
}

const hide = () => {
  visible.value = false
}

// 监听事件
onMounted(() => {
  emitter.on('showGuestCredentials', show)
})

onUnmounted(() => {
  emitter.off('showGuestCredentials', show)
})

// 使用本地数据或 props
const displayUid = () => localUid.value || props.uid
const displayPassword = () => localPassword.value || props.password

const handleClose = () => {
  // 点击遮罩层不关闭，必须点击确认按钮
}

const handleConfirm = () => {
  hide()
  emit('confirm')
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    toastSuccess($swal, t("message.copySuccess"))
  } catch (err) {
    // fallback
    const textarea = document.createElement('textarea')
    textarea.value = text
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    toastSuccess($swal, t("message.copySuccess"))
  }
}

defineExpose({
  show,
  hide
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.credentials-modal {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: 90vw;
  max-width: 360px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  text-align: center;
  margin-bottom: 20px;
}

.warning-icon {
  margin-bottom: 12px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.credentials-content {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.credential-item {
  margin-bottom: 12px;
}

.credential-item:last-child {
  margin-bottom: 0;
}

.credential-label {
  display: block;
  font-size: 12px;
  color: #666;
  margin-bottom: 6px;
  font-weight: 500;
}

.credential-value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
}

.value-text {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  word-break: break-all;
  flex: 1;
  margin-right: 8px;
}

.copy-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.copy-btn:hover {
  background: #e0e0e0;
  color: #333;
}

.copy-btn:active {
  transform: scale(0.95);
}

.warning-message {
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
}

.warning-message p {
  margin: 0;
  font-size: 13px;
  color: #d48806;
  line-height: 1.5;
  text-align: center;
}

.confirm-btn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(45deg, #1890ff, #40a9ff);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(24, 144, 255, 0.3);
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(24, 144, 255, 0.4);
}

.confirm-btn:active {
  transform: translateY(0);
}
</style>
